import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useOAuthAPI } from '../../../api/use-oauth-api';
import { getSwrFetcherWithDynamicUrl } from '../../../utils/get-swr-fetcher-with-dynamic-url';
import { OAuthConnectionInformation } from '../../integrations/types/types';
import { DisconnectGmailResponse } from '../invoice-pay-page-inner-wrapper';

const DISCONNECT_XERO_URL = '/api/oauth/disconnect?provider=xero';

export const useXeroConnection = () => {
  const { checkXeroConnection } = useOAuthAPI();
  const [isCheckingXeroConnection, setIsCheckingXeroConnection] = useState(true);
  const [xeroConnectionStatus, setXeroConnectionStatus] = useState<OAuthConnectionInformation | null>(null);
  const { trigger: triggerDisconnectXero, isMutating: isDisconnectingXero } = useSWRMutation<
    DisconnectGmailResponse,
    AxiosError,
    string,
    { urlSuffix: string }
  >(DISCONNECT_XERO_URL, getSwrFetcherWithDynamicUrl);

  const checkXeroConnectionStatus = async () => {
    setIsCheckingXeroConnection(true);

    try {
      const status = await checkXeroConnection();

      setXeroConnectionStatus(status);
    } catch (error) {
      setXeroConnectionStatus({
        connected: false,
        error: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsCheckingXeroConnection(false);
    }
  };

  const onDisconnectXero = async (connectionId: string) => {
    try {
      const result = await triggerDisconnectXero({ urlSuffix: `&connectionId=${connectionId}` });

      if (result.success) {
        await checkXeroConnectionStatus();

        toast.success('Xero disconnected successfully');
      }
    } catch (error) {
      Sentry.captureException(error);

      toast.error('Failed to disconnect Xero');
    }
  };

  // TODO: can this be moved to SWR?
  useEffect(() => {
    checkXeroConnectionStatus();
  }, []);

  return {
    xeroConnectionStatus,
    isCheckingXeroConnection,
    isDisconnectingXero,
    mutations: {
      onDisconnectXero,
    },
  };
};
